import React, { useMemo } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useConsentDraft } from '../../contexts/ConsentDraftContext';
import { areAllConsentsAnswered } from '../../utils/consentCompletion';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import './MainNav.css';

const MainNav: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get('studentId');
  const query = studentId ? `?studentId=${encodeURIComponent(studentId)}` : '';

  const { consentData } = useAuth();
  const { draft } = useConsentDraft();

  const definitions = consentData?.definitions ?? [];
  const showConsentsIncomplete =
    definitions.length > 0 && !areAllConsentsAnswered(definitions, draft);

  const consentsNavAriaLabel = useMemo(() => {
    const label = t('consents.tabConsents');
    if (!showConsentsIncomplete) return label;
    return `${label}. ${t('consents.navIncompleteConsentsAria')}`;
  }, [showConsentsIncomplete, t]);

  return (
    <header className="main-app-header" role="banner">
      <div className="main-app-header-inner">
        <span className="main-app-header-brand">{t('consents.navHeaderTitle')}</span>
        <nav className="main-app-nav" aria-label={t('consents.navAriaLabel')}>
          <NavLink
            to={`/${query}`}
            end
            className={({ isActive }) => `main-app-nav-link ${isActive ? 'active' : ''}`}
          >
            <span className="main-app-nav-link-icon" aria-hidden>
              🍽
            </span>
            <span className="main-app-nav-link-text">{t('consents.tabMeals')}</span>
          </NavLink>
          <NavLink
            to={`/consents${query}`}
            className={({ isActive }) => `main-app-nav-link ${isActive ? 'active' : ''}`}
            aria-label={consentsNavAriaLabel}
            title={showConsentsIncomplete ? t('consents.navIncompleteConsentsTitle') : undefined}
          >
            <span className="main-app-nav-link-icon" aria-hidden>
              📋
            </span>
            <span className="main-app-nav-link-text">{t('consents.tabConsents')}</span>
            {showConsentsIncomplete && (
              <span className="main-app-nav-incomplete-dot" aria-hidden />
            )}
          </NavLink>
        </nav>
        <div className="main-app-header-actions">
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};

export default MainNav;
